import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAddress } from 'viem';
import { useAuth } from '../lib/auth.js';

interface InjectedProvider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
}

const injected = (): InjectedProvider | null =>
  (window as unknown as { ethereum?: InjectedProvider }).ethereum ?? null;

export const Login = () => {
  const { signInWithSiwe } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const onConnect = async () => {
    setError(null);
    setPending(true);
    try {
      const eth = injected();
      if (!eth) throw new Error('No EVM wallet detected. Install MetaMask, Rabby, or similar.');
      const accounts = (await eth.request({ method: 'eth_requestAccounts' })) as string[];
      const account = accounts[0];
      if (!account) throw new Error('No account returned');
      const address = getAddress(account);
      await signInWithSiwe({
        address,
        sign: async (message) => {
          const sig = await eth.request({
            method: 'personal_sign',
            params: [message, address],
          });
          return sig as `0x${string}`;
        },
      });
      nav('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="login-page">
      <section className="hero login-card">
        <div>
          <p className="eyebrow">Paper-first AI trading</p>
          <h1>Launch a simulated trading agent in minutes.</h1>
          <p className="lead">
            Connect a wallet, choose guardrails, and watch the AI make paper decisions before any
            real capital is involved.
          </p>
          <div className="row" style={{ justifyContent: 'flex-start', marginTop: '1.5rem' }}>
            <button
              className="btn btn-primary"
              type="button"
              onClick={onConnect}
              disabled={pending}
            >
              {pending ? 'Requesting signature…' : 'Connect wallet'}
            </button>
            <span className="badge badge-success">Paper-first by default</span>
          </div>
          {error && <p style={{ color: '#fca5a5', marginTop: '1rem' }}>{error}</p>}
        </div>
        <div className="grid">
          <div className="card stat panel-strong">
            <span className="label">Getting started</span>
            <p className="stat-value">3 steps</p>
            <p className="muted">Wallet, risk profile, start paper loop.</p>
          </div>
          <div className="card stat">
            <span className="label">Paper equity</span>
            <p className="stat-value">$10k</p>
            <p className="muted">A safe sandbox before live trading.</p>
          </div>
          <div className="card stat">
            <span className="label">Infrastructure</span>
            <p className="stat-value">Cloudflare</p>
            <p className="muted">Workers, queues, D1, and live updates.</p>
          </div>
        </div>
      </section>
    </main>
  );
};
